<script setup lang="ts">
import { useField, useFieldValue } from 'vee-validate'
import { inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  MonitorovanyUkazovatelPublicResponse,
  MonitorovanyUkazovatelPublicResponseTypEnum as UkazovatelTypEnum,
  TextovaHodnotaSpecTypHodnotyEnum,
} from '/@src/client-public'
import { useSteps } from '/@src/composable/useSteps'
import { formatNumberInput } from '/@src/utils/format'

export interface UkazovatelProps {
  ukazovatel: MonitorovanyUkazovatelPublicResponse
  idx: number
}

const props = defineProps<UkazovatelProps>()

const { t } = useI18n()
const isChecked = ref(false)
const isReadOnly = inject<boolean>('isReadOnly')
const { activeStep } = useSteps()

const { resetField: resetFieldVychodiskovaHodnota } = useField(
  `projekt.ukazovatele[${props.idx}].vychodiskovaHodnota`
)
const { resetField: resetFieldCielovaHodnota } = useField(
  `projekt.ukazovatele[${props.idx}].cielovaHodnota`
)
const { value: checkBoxValue, setValue: setCheckBoxValue } = useField(
  `projekt.ukazovatele[${props.idx}].checkbox`
)
const vychodiskovaHodnotaTextova = useFieldValue(
  `projekt.ukazovatele[${props.idx}].vychodiskovaHodnota.textovaHodnota`
)
const cielovaHodnotaTextova = useFieldValue(
  `projekt.ukazovatele[${props.idx}].cielovaHodnota.textovaHodnota`
)
const vychodiskovaHodnotaLogicka = useFieldValue(
  `projekt.ukazovatele[${props.idx}].vychodiskovaHodnota.logickaHodnota`
)
const cielovaHodnotaLogicka = useFieldValue(
  `projekt.ukazovatele[${props.idx}].cielovaHodnota.logickaHodnota`
)
const vychodiskovaHodnotaCiselna = useFieldValue(
  `projekt.ukazovatele[${props.idx}].vychodiskovaHodnota.ciselnaHodnota`
)
const cielovaHodnotaCiselna = useFieldValue(
  `projekt.ukazovatele[${props.idx}].cielovaHodnota.ciselnaHodnota`
)

const getSelectOptions = (data: any) => {
  let output = []
  for (const i in data) {
    output.push({ value: i, label: data[i] })
  }
  return output
}

const LOGICAL_VALUE_OPTIONS = [
  { value: '', label: t('zoppm.step4.part7.radioDefault') },
  { value: true, label: t('zoppm.step4.part7.radioYes') },
  { value: false, label: t('zoppm.step4.part7.radioNo') },
]

watch(
  [
    vychodiskovaHodnotaTextova,
    cielovaHodnotaTextova,
    vychodiskovaHodnotaLogicka,
    cielovaHodnotaLogicka,
    vychodiskovaHodnotaCiselna,
    cielovaHodnotaCiselna,
  ],
  () => {
    if (
      !!(
        vychodiskovaHodnotaTextova.value ||
        cielovaHodnotaTextova.value ||
        (vychodiskovaHodnotaLogicka.value !== null
          ? JSON.stringify(vychodiskovaHodnotaLogicka.value)
          : null) ||
        (cielovaHodnotaLogicka.value !== null
          ? JSON.stringify(cielovaHodnotaLogicka.value)
          : null) ||
        vychodiskovaHodnotaCiselna.value ||
        cielovaHodnotaCiselna.value
      )
    ) {
      isChecked.value = true
      setCheckBoxValue(true)
    }
  }
)

watch(
  () => activeStep.value,
  () => {
    if (isReadOnly) {
      isChecked.value = false
      setCheckBoxValue(false)
    }
  }
)
</script>

<template>
  <div :key="isReadOnly && activeStep">
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.ukazovatelId`"
      :value="ukazovatel.ukazovatelRef.ukazovatelId"
    >
      <IDSKInput
        v-bind="slotProps"
        :id="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.ukazovatelId`"
        readonly
        hidden
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.kod`"
      :value="ukazovatel.ukazovatelRef.kod"
    >
      <IDSKInput
        v-bind="slotProps"
        :id="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.kod`"
        readonly
        hidden
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.nazov`"
      :value="ukazovatel.ukazovatelRef.nazov"
    >
      <IDSKInput
        v-bind="slotProps"
        :id="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.nazov`"
        readonly
        hidden
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.mernaJednotka`"
      :value="ukazovatel.ukazovatelRef.mernaJednotka"
    >
      <IDSKInput
        v-bind="slotProps"
        :id="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelRef.mernaJednotka`"
        readonly
        hidden
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelVyzvyId`"
      :value="ukazovatel.ukazovatelVyzvyId"
    >
      <IDSKInput
        v-bind="slotProps"
        :id="`projekt.ukazovatele[${idx}].ukazovatelVyzvyRef.ukazovatelVyzvyId`"
        readonly
        hidden
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.validateTyp`"
      :value="ukazovatel?.validateTyp"
    >
      <IDSKInput v-bind="slotProps" readonly hidden />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`projekt.ukazovatele[${idx}].cielovaHodnota.validateTyp`"
      :value="ukazovatel?.validateTyp"
    >
      <IDSKInput v-bind="slotProps" readonly hidden />
    </ValidationWrapper>
    <IDSKCheckbox
      v-if="!ukazovatel.casZneplatnenia"
      :id="`projekt.ukazovatele[${idx}].checkbox`"
      :name="`projekt.ukazovatele[${idx}].checkbox`"
      :checked="isChecked ? true : false"
      :is-in-checkboxes="true"
      @is-checked="
        (value) => {
          isChecked = value
          setCheckBoxValue(value)

          if (value === false) {
            resetFieldVychodiskovaHodnota()
            resetFieldCielovaHodnota()
          }
        }
      "
    >
      <template #default>
        <IDSKHeading v-if="!ukazovatel.casZneplatnenia" as="h3" variant="m">
          {{ ukazovatel.ukazovatelRef.nazov }}
        </IDSKHeading>
      </template>

      <template v-if="ukazovatel.popis" #visible-content>
        <IDSKParagraph>{{ ukazovatel.popis }}</IDSKParagraph>
      </template>

      <template #hidden-content>
        <template v-if="ukazovatel.typ === UkazovatelTypEnum.Kvantitativny">
          <div>
            <ValidationWrapper
              v-slot="slotProps"
              :format-value="formatNumberInput"
              :name="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.ciselnaHodnota`"
            >
              <IDSKInput
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.ciselnaHodnota`"
                :label="t('zoppm.step4.part1.defaultValue')"
                :hint="ukazovatel.ciselnaHodnotaSpec.mernaJednotka"
              />
            </ValidationWrapper>
            <ValidationWrapper
              v-slot="slotProps"
              :format-value="formatNumberInput"
              :name="`projekt.ukazovatele[${idx}].cielovaHodnota.ciselnaHodnota`"
            >
              <IDSKInput
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].cielovaHodnota.ciselnaHodnota`"
                :label="t('zoppm.step4.part1.finalValue')"
                :hint="ukazovatel.ciselnaHodnotaSpec.mernaJednotka"
              />
            </ValidationWrapper>
          </div>
        </template>

        <template v-if="ukazovatel.typ === UkazovatelTypEnum.Kvalitativny">
          <div
            v-if="
              ukazovatel.textovaHodnotaSpec.typHodnoty ===
              TextovaHodnotaSpecTypHodnotyEnum.PravdaNepravda
            "
          >
            <ValidationWrapper
              v-slot="slotProps"
              :name="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.logickaHodnota`"
              :value="LOGICAL_VALUE_OPTIONS[0].value"
            >
              <IDSKRadios
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.logickaHodnota`"
                :default-value="LOGICAL_VALUE_OPTIONS[0].value"
                :label="t('zoppm.step4.part7.defaultValue')"
                :options="LOGICAL_VALUE_OPTIONS"
              />
            </ValidationWrapper>
            <ValidationWrapper
              v-slot="slotProps"
              :name="`projekt.ukazovatele[${idx}].cielovaHodnota.logickaHodnota`"
              :value="LOGICAL_VALUE_OPTIONS[0].value"
            >
              <IDSKRadios
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].cielovaHodnota.logickaHodnota`"
                :default-value="LOGICAL_VALUE_OPTIONS[0].value"
                :label="t('zoppm.step4.part7.finalValue')"
                :options="LOGICAL_VALUE_OPTIONS"
              />
            </ValidationWrapper>
          </div>

          <div
            v-if="
              ukazovatel.textovaHodnotaSpec.typHodnoty ===
              TextovaHodnotaSpecTypHodnotyEnum.ZoznamHodnota
            "
          >
            <ValidationWrapper
              v-slot="{ 'onUpdate:modelValue': update, ...slotProps }"
              :name="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.textovaHodnota`"
            >
              <IDSKSelect
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.textovaHodnota`"
                :label="t('zoppm.step4.part2.defaultValue')"
                full-width
                option-object-label="label"
                option-object-value="value"
                :options="getSelectOptions(ukazovatel.textovaHodnotaSpec.povoleneHodnoty)"
                @update:model-value="(option) => update(option.value)"
              />
            </ValidationWrapper>
            <ValidationWrapper
              v-slot="{ 'onUpdate:modelValue': update, ...slotProps }"
              :name="`projekt.ukazovatele[${idx}].cielovaHodnota.textovaHodnota`"
            >
              <IDSKSelect
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].cielovaHodnota.textovaHodnota`"
                :label="t('zoppm.step4.part2.finalValue')"
                full-width
                option-object-label="label"
                option-object-value="value"
                :options="getSelectOptions(ukazovatel.textovaHodnotaSpec.povoleneHodnoty)"
                @update:model-value="(option) => update(option.value)"
              />
            </ValidationWrapper>
          </div>

          <div
            v-if="
              ukazovatel.textovaHodnotaSpec.typHodnoty ===
              TextovaHodnotaSpecTypHodnotyEnum.TextovaHodnota
            "
          >
            <ValidationWrapper
              v-slot="slotProps"
              :name="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.textovaHodnota`"
            >
              <IDSKTextarea
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].vychodiskovaHodnota.textovaHodnota`"
                :label="t('zoppm.step4.part8.defaultValue')"
              />
            </ValidationWrapper>
            <ValidationWrapper
              v-slot="slotProps"
              :name="`projekt.ukazovatele[${idx}].cielovaHodnota.textovaHodnota`"
            >
              <IDSKTextarea
                v-bind="slotProps"
                :id="`projekt.ukazovatele[${idx}].cielovaHodnota.textovaHodnota`"
                :label="t('zoppm.step4.part8.finalValue')"
              />
            </ValidationWrapper>
          </div>
        </template>
      </template>
    </IDSKCheckbox>
  </div>
</template>
